/**
 * tourStats.ts — the tour-stats badge calculation, shared by AtlasPage and
 * DayOfPage (P2e).
 *
 * The Atlas header badge ("N stops · M played · X km") was born in AtlasPage.
 * The user decision for P2e ("home contextuelle + widgets V1") asks that the
 * Day-of page's BEFORE mode shows the SAME badge — reusing the calculation
 * through a shared helper, never a copy that could drift. AtlasPage keeps its
 * own map geometry (projection, labels, segments) but the badge numbers now
 * come from HERE on both pages.
 *
 * Rules honoured (byte-identical to the original AtlasPage maths):
 *   - a "stop" is a show whose venue_id points to a GEOFENCED venue (finite
 *     lat/lng, not (0,0), |lat|≤90, |lng|≤180) — a show without a geocoded
 *     venue is not a stop (it never invents coordinates);
 *   - shows sort by date, undated ones last ('9999-99-99');
 *   - "played" = a stop whose date is before TODAY'S LOCAL MIDNIGHT
 *     (`new Date('YYYY-MM-DD')` = UTC midnight, same parse the Atlas map
 *     uses for its past/next segment states);
 *   - km = haversine sum over CONSECUTIVE geocoded stops (a non-geocoded
 *     show cuts the chain — the distance never crosses a missing stop).
 */
import type { ShowDTO, VenueDTO } from '../api';

/** Undated shows sort LAST instead of first (''). */
const NOT_DATED = '9999-99-99';

export function isGeocoded(v?: VenueDTO | null): v is VenueDTO {
  if (!v) return false;
  const lat = Number(v.lat);
  const lng = Number(v.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (lat === 0 && lng === 0) return false;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return false;
  return true;
}

export function haversineKm(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}

export interface TourStats {
  /** Geocoded stops (shows whose venue carries real coordinates). */
  stops: number;
  /** Stops dated before today's local midnight. */
  played: number;
  /** Haversine km across consecutive geocoded stops. */
  km: number;
}

/** The badge numbers of a band, from its shows + the venue catalog. */
export function tourStatsOf(
  shows: readonly ShowDTO[],
  venues: readonly VenueDTO[],
  now: number = Date.now(),
): TourStats {
  const venueById = new Map<string, VenueDTO>(venues.map((v) => [String(v.id), v]));
  const refDay = new Date(now);
  refDay.setHours(0, 0, 0, 0);
  const todayMs = refDay.getTime();

  const sorted = [...shows].sort((a, b) => {
    const ka = a.date || NOT_DATED;
    const kb = b.date || NOT_DATED;
    return ka < kb ? -1 : ka > kb ? 1 : 0;
  });

  // Geocoded shows in date order (the stops chain).
  const geo: { lat: number; lng: number; past: boolean }[] = [];
  for (const s of sorted) {
    if (!s.date || s.venue_id == null || s.venue_id === '') continue;
    const v = venueById.get(String(s.venue_id));
    if (!isGeocoded(v)) continue;
    geo.push({
      lat: Number(v.lat),
      lng: Number(v.lng),
      past: new Date(s.date).getTime() < todayMs,
    });
  }

  let km = 0;
  for (let i = 0; i < geo.length - 1; i++) {
    km += haversineKm(geo[i].lat, geo[i].lng, geo[i + 1].lat, geo[i + 1].lng);
  }

  return {
    stops: geo.length,
    played: geo.filter((g) => g.past).length,
    km,
  };
}

/**
 * The badge RULE in one place: the km variant only from 2 stops (one leg).
 * Returns the i18n key (`atlas.badge` / `atlas.badgeKm` — the SAME strings on
 * both pages, no duplicate translations) and its variables.
 */
export function tourStatsBadge(stats: TourStats, locale: string): {
  key: string;
  vars: { stops: number; played: number; km?: string };
} {
  return stats.stops >= 2
    ? {
        key: 'atlas.badgeKm',
        vars: {
          stops: stats.stops,
          played: stats.played,
          km: Math.round(stats.km).toLocaleString(locale),
        },
      }
    : { key: 'atlas.badge', vars: { stops: stats.stops, played: stats.played } };
}
